import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import accountingApi from '../services/accountingApi';

interface AccountHead {
    id: number;
    name: string;
    account_type: string;
    groups_count?: number;
}

function AccountHeadsIndex() {
    const [accountHeads, setAccountHeads] = useState<AccountHead[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        loadAccountHeads();
    }, []);

    const loadAccountHeads = async () => {
        try {
            const response = await accountingApi.getAccountHeads();
            if (response.success) {
                const heads = Array.isArray(response.data) ? response.data : (response.data?.account_heads || response.data?.data || []);
                setAccountHeads(heads);
            } else {
                console.error('Error loading account heads:', response.message);
            }
        } catch (error) {
            console.error('Error loading account heads:', error);
        } finally {
            setLoading(false);
        }
    };

    // Group account heads by type and get the main head for each type
    const accountTypes = [
        { type: 'Assets', icon: '💰', color: 'blue' as const, description: 'What the business owns' },
        { type: 'Liabilities', icon: '📊', color: 'red' as const, description: 'What the business owes' },
        { type: 'Income', icon: '💵', color: 'green' as const, description: 'Revenue from operations' },
        { type: 'Expense', icon: '💸', color: 'orange' as const, description: 'Costs of doing business' },
        { type: 'Equity', icon: '🏦', color: 'purple' as const, description: 'Owner\'s stake in the business' }
    ];

    const getTypeData = (type: string) => {
        const headsOfType = accountHeads.filter(h => h.account_type === type);
        const totalGroups = headsOfType.reduce((sum, h) => sum + (h.groups_count || 0), 0);
        return { heads: headsOfType, totalGroups };
    };

    const getCardColor = (color: 'blue' | 'red' | 'green' | 'orange' | 'purple') => {
        const colors = {
            'blue': 'bg-blue-50 border-blue-200',
            'red': 'bg-red-50 border-red-200',
            'green': 'bg-green-50 border-green-200',
            'orange': 'bg-orange-50 border-orange-200',
            'purple': 'bg-purple-50 border-purple-200'
        };
        return colors[color] || 'bg-slate-50 border-slate-200';
    };

    if (loading) return <div className="p-8 text-center">Loading...</div>;

    return (
        <div className="max-w-7xl mx-auto px-4 py-8">
            <div className="mb-8">
                <div className="flex items-center gap-3 mb-2">
                    <Link to="/accounting" className="p-2 text-slate-400 hover:text-indigo-600 rounded-lg transition-colors">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                        </svg>
                    </Link>
                    <h1 className="text-3xl font-bold text-slate-800">Account Heads</h1>
                </div>
                <p className="text-slate-600">Predefined account categories for financial management</p>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
                <h2 className="text-xl font-bold text-slate-800 mb-6">Account Categories</h2>

                <div className="space-y-6">
                    {accountTypes.map(({ type, icon, color, description }) => {
                        const { heads, totalGroups } = getTypeData(type);

                        if (heads.length === 0) return null;

                        return (
                            <div key={type}>
                                <div className="flex items-center gap-2 mb-3">
                                    <span className="text-2xl">{icon}</span>
                                    <h3 className="text-lg font-bold text-slate-800">{type}</h3>
                                </div>

                                <div className={`border rounded-lg p-4 ${getCardColor(color)}`}>
                                    <h4 className="font-bold text-slate-900 mb-1">{type}</h4>
                                    <p className="text-sm text-slate-600 mb-3">{description}</p>

                                    <div className="flex items-center justify-between">
                                        <span className="text-sm text-slate-500">
                                            {totalGroups} account group{totalGroups !== 1 ? 's' : ''}
                                        </span>
                                        <Link
                                            to={`/accounting/account-groups?type=${type}`}
                                            className="text-sm font-medium text-indigo-600 hover:text-indigo-800"
                                        >
                                            View Details
                                        </Link>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>

                {accountHeads.length === 0 && (
                    <div className="text-center py-12">
                        <p className="text-slate-400 italic">No account heads found</p>
                    </div>
                )}
            </div>
        </div>
    );
}

export default AccountHeadsIndex;
